import { useCallback, useEffect, useState } from 'react';
import { PROJECT_ID, UPDATE_CONTACT_CONFIG_URL, CONTACT_CONFIG_TABLE } from '@/lib/supabase';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

export default function SettingsTab() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    setFeedback(null);
    try {
      const url = `${SUPABASE_URL}/rest/v1/${CONTACT_CONFIG_TABLE}?rdy_project_id=eq.${PROJECT_ID}&select=to`;
      const res = await fetch(url, {
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
      });
      const text = await res.text();
      if (!res.ok) {
        throw new Error('通知先設定の取得に失敗しました。');
      }
      let rows: unknown = [];
      try {
        rows = JSON.parse(text);
      } catch {
        rows = [];
      }
      const first = Array.isArray(rows) ? (rows[0] as { to?: string } | undefined) : undefined;
      setEmail(first?.to ?? '');
    } catch {
      setLoadError('通知先設定の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const res = await fetch(UPDATE_CONTACT_CONFIG_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: PROJECT_ID, to: email.trim() }),
      });
      const text = await res.text();
      let json: unknown = null;
      try {
        json = JSON.parse(text);
      } catch {
        json = null;
      }
      const hasError =
        json && typeof json === 'object' && (('error' in json && (json as Record<string, unknown>).error) || (json as Record<string, unknown>).success === false);
      if (!res.ok || hasError) {
        const message =
          (json && typeof json === 'object'
            ? String(
                (json as Record<string, unknown>).error ||
                  (json as Record<string, unknown>).message ||
                  '',
              )
            : '') || '設定の保存に失敗しました。';
        throw new Error(message);
      }
      setFeedback({ type: 'success', text: '通知先メールアドレスを保存しました。' });
    } catch (e) {
      setFeedback({
        type: 'error',
        text: e instanceof Error && e.message ? e.message : '設定の保存に失敗しました。',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-6 md:p-8">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#7c3aed] to-[#a855f7] flex items-center justify-center">
            <i className="ri-settings-3-line text-white"></i>
          </span>
          <div>
            <h2 className="text-base font-extrabold text-white">お問い合わせ通知設定</h2>
            <p className="text-sm text-[#a0a0b0]">フォーム送信時の通知先メールアドレスを設定します。</p>
          </div>
        </div>

        {loading ? (
          <div className="mt-8 py-10 text-center text-[#a0a0b0]">
            <i className="ri-loader-4-line animate-spin text-2xl"></i>
          </div>
        ) : loadError ? (
          <div className="mt-8 text-center">
            <p className="text-[#e8e8f0]">{loadError}</p>
            <button
              type="button"
              onClick={loadConfig}
              className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-xl h-10 px-5 text-sm font-medium bg-[#1a1a2e] border border-[#2a2a3e] text-[#e8e8f0] hover:bg-[#2a2a3e] transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              再読み込み
            </button>
          </div>
        ) : (
          <div className="mt-8">
            <label htmlFor="notify-email" className="block text-sm font-medium text-[#e8e8f0]">
              通知先メールアドレス
            </label>
            <input
              id="notify-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contact@example.com"
              className="mt-2 w-full rounded-xl bg-[#12121f] border border-[#2a2a3e] px-4 py-3 text-sm text-white placeholder:text-[#5a5a6a] focus:outline-none focus:border-[#7c3aed] transition-colors"
            />

            {feedback && (
              <div
                className={`mt-4 flex items-center gap-2 rounded-xl px-4 py-3 text-sm ${
                  feedback.type === 'success'
                    ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
                    : 'bg-red-500/10 text-red-400 border border-red-500/30'
                }`}
              >
                <i className={feedback.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}></i>
                {feedback.text}
              </div>
            )}

            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-xl h-11 px-6 text-sm font-semibold bg-gradient-to-r from-[#7c3aed] to-[#a855f7] text-white transition-all duration-300 hover:brightness-110 disabled:opacity-60 cursor-pointer"
            >
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
              保存する
            </button>
          </div>
        )}
      </div>
    </div>
  );
}